'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import {
  chatApi,
  useCreateRealtimeTicketMutation,
} from '@/features/chat/api/chatApi';
import { ChatRealtimeClient } from '@/features/chat/realtime/chatRealtimeClient';
import { useAppDispatch } from '@/store/hooks';
import { notify } from '@web/utils';

import type { ActiveChat } from '@/app/(protected)/[workspace_slug]/chat/ChatContext';
import type { ChatRealtimeEvent } from '@/features/chat/types';

type ChatRealtimeContextValue = {
  registerActiveChat: (activeChat: ActiveChat) => () => void;
};

const ChatRealtimeContext = createContext<ChatRealtimeContextValue | null>(
  null,
);

function isRealConversationType(type: ActiveChat['type']) {
  return ['general_channel', 'job_channel', 'direct_message'].includes(type);
}

function getConversationMessageTag(
  type: ActiveChat['type'],
  id: string,
  jobId?: string,
) {
  if (type === 'general_channel') {
    return { type: 'ChatMessages' as const, id: 'GENERAL' };
  }

  if (type === 'job_channel' && jobId) {
    return { type: 'ChatMessages' as const, id: `JOB:${jobId}` };
  }

  if (type === 'direct_message') {
    return { type: 'ChatMessages' as const, id: `DM:${id}` };
  }

  return null;
}

function getConversationId(activeChat: ActiveChat) {
  return isRealConversationType(activeChat.type) ? activeChat.id : null;
}

export function ChatRealtimeProvider({
  children,
  enabled = true,
}: {
  children: React.ReactNode;
  enabled?: boolean;
}) {
  const dispatch = useAppDispatch();
  const [createTicket] = useCreateRealtimeTicketMutation();
  const clientRef = useRef<ChatRealtimeClient | null>(null);
  const subscribedConversationRef = useRef<string | null>(null);
  const requestedConversationRef = useRef<string | null>(null);
  const activeMessageTagRef = useRef<ReturnType<
    typeof getConversationMessageTag
  > | null>(null);

  const syncSubscription = useCallback((nextConversationId: string | null) => {
    const client = clientRef.current;
    if (!client) {
      return;
    }

    const previousConversationId = subscribedConversationRef.current;

    if (
      previousConversationId &&
      previousConversationId !== nextConversationId
    ) {
      client.unsubscribe(previousConversationId);
    }

    if (nextConversationId && previousConversationId !== nextConversationId) {
      client.subscribe(nextConversationId);
    }

    subscribedConversationRef.current = nextConversationId;
  }, []);

  const handleEvent = useCallback(
    (event: ChatRealtimeEvent) => {
      if (event.type === 'chat.permission.revoked') {
        dispatch(
          chatApi.util.invalidateTags([
            { type: 'ChatDestinations', id: 'LIST' },
            { type: 'ChatMessages', id: 'GENERAL' },
            { type: 'ChatMessages', id: 'STREAM' },
            { type: 'ChatMessages', id: 'UNREAD' },
            { type: 'ChatMessages', id: 'MENTIONS' },
          ]),
        );

        if (event.scope === 'workspace') {
          notify({
            type: 'error',
            message: 'Chat access was revoked for this workspace.',
          });
        }

        return;
      }

      if (event.type.startsWith('chat.message.')) {
        const currentMessageTag = activeMessageTagRef.current;
        const tags = [
          { type: 'ChatDestinations' as const, id: 'LIST' },
          { type: 'ChatMessages' as const, id: 'STREAM' },
        ];

        if (currentMessageTag) {
          tags.push(currentMessageTag);
        }

        dispatch(chatApi.util.invalidateTags(tags));
        return;
      }

      if (
        event.type.startsWith('chat.context_tag.') ||
        event.type === 'chat.destination.changed'
      ) {
        dispatch(
          chatApi.util.invalidateTags([
            { type: 'ChatDestinations', id: 'LIST' },
            { type: 'ChatMessages', id: 'STREAM' },
            { type: 'ChatMessages', id: 'UNREAD' },
            { type: 'ChatMessages', id: 'MENTIONS' },
          ]),
        );
        return;
      }

      if (event.type === 'chat.destination.unread_changed') {
        dispatch(
          chatApi.util.invalidateTags([
            { type: 'ChatDestinations', id: 'LIST' },
            { type: 'ChatMessages', id: 'STREAM' },
            { type: 'ChatMessages', id: 'UNREAD' },
            { type: 'ChatMessages', id: 'MENTIONS' },
          ]),
        );
      }
    },
    [dispatch],
  );

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      return;
    }

    let isDisposed = false;

    const connect = async () => {
      try {
        const ticket = await createTicket().unwrap();

        if (isDisposed || !ticket.data) {
          return;
        }

        const client = new ChatRealtimeClient({
          websocketUrl: ticket.data.websocket_url,
          onEvent: handleEvent,
          onOpen: () => {
            const requestedConversationId = requestedConversationRef.current;
            if (requestedConversationId) {
              client.subscribe(requestedConversationId);
              subscribedConversationRef.current = requestedConversationId;
            }
          },
        });

        clientRef.current = client;
        client.connect();
      } catch {
        if (!isDisposed) {
          notify({
            type: 'error',
            message: 'Unable to connect to chat realtime updates.',
          });
        }
      }
    };

    void connect();

    return () => {
      isDisposed = true;
      clientRef.current?.disconnect();
      clientRef.current = null;
      subscribedConversationRef.current = null;
    };
  }, [createTicket, enabled, handleEvent]);

  const registerActiveChat = useCallback(
    (activeChat: ActiveChat) => {
      const nextConversationId = getConversationId(activeChat);
      requestedConversationRef.current = nextConversationId;
      activeMessageTagRef.current = getConversationMessageTag(
        activeChat.type,
        activeChat.id,
        activeChat.jobId,
      );
      syncSubscription(nextConversationId);

      return () => {
        if (requestedConversationRef.current !== nextConversationId) {
          return;
        }

        requestedConversationRef.current = null;
        activeMessageTagRef.current = null;
        syncSubscription(null);
      };
    },
    [syncSubscription],
  );

  const value = useMemo(
    () => ({
      registerActiveChat,
    }),
    [registerActiveChat],
  );

  return (
    <ChatRealtimeContext.Provider value={value}>
      {children}
    </ChatRealtimeContext.Provider>
  );
}

export function useChatRealtimeSubscription(activeChat: ActiveChat) {
  const context = useContext(ChatRealtimeContext);

  useEffect(() => {
    if (!context) {
      return;
    }

    return context.registerActiveChat(activeChat);
  }, [activeChat, context]);
}
